import { useEffect, useMemo, useState } from "react";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import { useNavigate } from "react-router-dom";
import {
    getScopeLabel,
    getEvidenceStatusLabel,
} from "../utils/pmoLabels";
import {
    PMO_BADGE_TONES,
    getPmoTooltip,
    getTaskStatusTone,
    getReviewStatusTone,
    getRouteStatusTone,
    getEvidenceStatusTone,
    getHealthTone,
} from "../utils/pmoStyles";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { API_BASE } from "../config/orbisConfig";



function safeNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function formatDate(value) {
    if (!value) return "Sin fecha";

    const d = new Date(String(value).replace(" ", "T"));

    if (Number.isNaN(d.getTime())) return value;

    return d.toLocaleString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function initials(name = "Usuario") {
    return (
        String(name)
            .trim()
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((x) => x[0]?.toUpperCase())
            .join("") || "U"
    );
}

function healthColor(value) {
    const h = String(value || "").toLowerCase();

    if (h === "rojo") return "#DC2626";
    if (h === "amarillo") return "#F59E0B";
    if (h === "verde") return "#16A34A";

    return "#64748B";
}

function KpiCard({ title, value, subtitle, color = "#0F172A", icon = "◉" }) {
    return (
        <div
            style={{
                borderRadius: 22,
                background: "#FFFFFF",
                border: "1px solid #E2E8F0",
                padding: 18,
                boxShadow: "0 18px 38px rgba(15,23,42,.06)",
                position: "relative",
                overflow: "hidden",
            }}
        >
            <div
                style={{
                    position: "absolute",
                    right: -30,
                    top: -30,
                    width: 96,
                    height: 96,
                    borderRadius: 999,
                    background: `${color}14`,
                }}
            />

            <div style={{ position: "relative", display: "flex", justifyContent: "space-between", gap: 12 }}>
                <div>
                    <div
                        style={{
                            color: "#64748B",
                            fontSize: 11,
                            fontWeight: 950,
                            textTransform: "uppercase",
                            letterSpacing: ".04em",
                        }}
                    >
                        {title}
                    </div>

                    <div
                        style={{
                            marginTop: 10,
                            color,
                            fontSize: 34,
                            fontWeight: 950,
                            letterSpacing: "-.05em",
                            lineHeight: 1,
                        }}
                    >
                        {value}
                    </div>

                    <div
                        style={{
                            marginTop: 10,
                            color: "#64748B",
                            fontSize: 12,
                            fontWeight: 750,
                            lineHeight: 1.35,
                        }}
                    >
                        {subtitle}
                    </div>
                </div>

                <div
                    style={{
                        width: 42,
                        height: 42,
                        borderRadius: 16,
                        background: `${color}12`,
                        color,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,
                        fontWeight: 950,
                        border: `1px solid ${color}22`,
                    }}
                >
                    {icon}
                </div>
            </div>
        </div>
    );
}

function FitBounds({ points = [] }) {
    const map = useMap();

    useEffect(() => {
        const valid = points
            .map((ev) => [safeNumber(ev.latitude, null), safeNumber(ev.longitude, null)])
            .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));

        if (!valid.length) return;

        if (valid.length === 1) {
            map.setView(valid[0], 15);
            return;
        }

        map.fitBounds(valid, {
            padding: [50, 50],
            maxZoom: 16,
        });
    }, [map, points]);

    return null;
}

function createEvidenceIcon(ev, active = false) {
    const initialsText = initials(ev.uploaded_by_name || ev.owner_name || ev.task_name);
    const color = healthColor(ev.project_health_status);
    const size = active ? 42 : 34;
    const fontSize = active ? 13 : 11;
    const border = active ? 3 : 2;

    return L.divIcon({
        className: "",
        html: `
            <div style="
                width:${size}px;
                height:${size}px;
                border-radius:999px;
                background:${color};
                color:#fff;
                display:flex;
                align-items:center;
                justify-content:center;
                font-size:${fontSize}px;
                font-weight:950;
                border:${border}px solid #fff;
                box-shadow:0 14px 30px rgba(15,23,42,.28);
            ">
                ${initialsText}
            </div>
        `,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        popupAnchor: [0, -Math.round(size / 2)],
    });
}

function EvidenceMapCanvas({ evidences = [], selectedEvidenceId, onSelect }) {
    const validPoints = evidences
        .map((ev) => ({
            ...ev,
            lat: safeNumber(ev.latitude, null),
            lng: safeNumber(ev.longitude, null),
        }))
        .filter((ev) => Number.isFinite(ev.lat) && Number.isFinite(ev.lng));

    const defaultCenter = validPoints.length ? [validPoints[0].lat, validPoints[0].lng] : [25.5439, -103.5264];

    const projectGroups = useMemo(() => {
        const map = new Map();

        validPoints.forEach((ev) => {
            const key = ev.project_id || "sin-proyecto";

            if (!map.has(key)) {
                map.set(key, {
                    project_id: ev.project_id,
                    project_name: ev.project_name,
                    project_code: ev.project_code,
                    count: 0,
                });
            }

            map.get(key).count += 1;
        });

        return Array.from(map.values());
    }, [validPoints]);

    return (
        <div
            style={{
                borderRadius: 30,
                background: "#FFFFFF",
                border: "1px solid #CBD5E1",
                minHeight: 560,
                position: "relative",
                overflow: "hidden",
                boxShadow: "0 24px 65px rgba(15,23,42,.10)",
            }}
        >
            <div
                style={{
                    position: "absolute",
                    left: 24,
                    top: 22,
                    zIndex: 500,
                    padding: "12px 14px",
                    borderRadius: 18,
                    background: "rgba(255,255,255,.92)",
                    border: "1px solid rgba(226,232,240,.9)",
                    boxShadow: "0 14px 30px rgba(15,23,42,.08)",
                }}
            >
                <div style={{ fontSize: 11, color: "#64748B", fontWeight: 950, textTransform: "uppercase" }}>
                    Mapa real de evidencias
                </div>
                <div style={{ marginTop: 4, fontSize: 20, color: "#0F172A", fontWeight: 950 }}>
                    {validPoints.length} punto(s)
                </div>
            </div>

            <div
                style={{
                    position: "absolute",
                    right: 24,
                    top: 22,
                    zIndex: 500,
                    display: "grid",
                    gap: 8,
                    maxWidth: 280,
                }}
            >
                {projectGroups.slice(0, 4).map((p) => (
                    <div
                        key={p.project_id || p.project_name}
                        style={{
                            padding: "9px 11px",
                            borderRadius: 16,
                            background: "rgba(255,255,255,.92)",
                            border: "1px solid rgba(226,232,240,.9)",
                            color: "#334155",
                            fontSize: 11,
                            fontWeight: 850,
                            boxShadow: "0 10px 24px rgba(15,23,42,.06)",
                        }}
                    >
                        {p.project_name || "Sin proyecto"} · {p.count} evidencia(s)
                    </div>
                ))}
            </div>

            <MapContainer
                center={defaultCenter}
                zoom={13}
                scrollWheelZoom
                style={{
                    height: 560,
                    width: "100%",
                    borderRadius: 30,
                    zIndex: 1,
                }}
            >
                <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                <FitBounds points={validPoints} />

                {validPoints.map((ev) => {
                    const active = String(selectedEvidenceId || "") === String(ev.id || "");

                    return (
                        <Marker
                            key={ev.id}
                            position={[ev.lat, ev.lng]}
                            icon={createEvidenceIcon(ev, active)}
                            eventHandlers={{
                                click: () => onSelect?.(ev),
                            }}
                        >
                            <Popup>
                                <div style={{ minWidth: 220 }}>
                                    <div style={{ fontWeight: 800, marginBottom: 4 }}>
                                        {ev.wbs_code ? `${ev.wbs_code} · ` : ""}
                                        {ev.task_name || "Tarea"}
                                    </div>

                                    <div style={{ fontSize: 12, marginBottom: 4 }}>
                                        Proyecto: {ev.project_name || "Sin proyecto"}
                                    </div>

                                    <div style={{ fontSize: 12, marginBottom: 4 }}>
                                        Subido por: {ev.uploaded_by_name || "Sin usuario"}
                                    </div>

                                    <div style={{ fontSize: 11, color: "#64748B" }}>
                                        {ev.latitude}, {ev.longitude}
                                    </div>
                                </div>
                            </Popup>
                        </Marker>
                    );
                })}
            </MapContainer>

            {!validPoints.length ? (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 600,
                        pointerEvents: "none",
                    }}
                >
                    <div
                        style={{
                            maxWidth: 420,
                            textAlign: "center",
                            padding: 26,
                            borderRadius: 24,
                            background: "rgba(255,255,255,.92)",
                            border: "1px dashed #CBD5E1",
                            color: "#64748B",
                            fontSize: 13,
                            fontWeight: 800,
                            lineHeight: 1.5,
                        }}
                    >
                        Todavía no hay evidencias con coordenadas para este filtro. Cuando se capture ubicación desde una tarea,
                        aparecerá aquí.
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function EvidenceCard({ evidence, onOpenTask, onOpenMaps }) {
    if (!evidence) {
        return (
            <div
                style={{
                    borderRadius: 26,
                    background: "#FFFFFF",
                    border: "1px solid #E2E8F0",
                    padding: 20,
                    color: "#64748B",
                    fontSize: 13,
                    fontWeight: 800,
                    boxShadow: "0 18px 45px rgba(15,23,42,.06)",
                }}
            >
                Selecciona un marcador del mapa para ver el detalle de la evidencia.
            </div>
        );
    }

    const evidenceTitle = evidence.title || evidence.evidence_title || evidence.file_name || "";

    return (
        <div
            style={{
                borderRadius: 26,
                background: "#FFFFFF",
                border: "1px solid #E2E8F0",
                padding: 20,
                boxShadow: "0 18px 45px rgba(15,23,42,.06)",
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <div>
                    <div
                        style={{
                            color: "#64748B",
                            fontSize: 11,
                            fontWeight: 950,
                            textTransform: "uppercase",
                            letterSpacing: ".04em",
                        }}
                    >
                        Evidencia seleccionada
                    </div>

                    <h2
                        style={{
                            margin: "8px 0 0",
                            color: "#0F172A",
                            fontSize: 21,
                            fontWeight: 950,
                            letterSpacing: "-.03em",
                        }}
                    >
                        {evidence.task_name
                            ? `${evidence.wbs_code ? `${evidence.wbs_code} · ` : ""}${evidence.task_name}`
                            : evidenceTitle || "Evidencia georreferenciada"}
                    </h2>

                    {evidenceTitle ? (
                        <div
                            style={{
                                marginTop: 6,
                                color: "#64748B",
                                fontSize: 12,
                                fontWeight: 800,
                            }}
                        >
                            Evidencia: {evidenceTitle}
                        </div>
                    ) : null}
                </div>

                <div
                    style={{
                        width: 44,
                        height: 44,
                        borderRadius: 16,
                        background: "#EFF6FF",
                        color: "#1D4ED8",
                        border: "1px solid #BFDBFE",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 19,
                        fontWeight: 950,
                        flex: "0 0 auto",
                    }}
                >
                    ⌖
                </div>
            </div>

            <div
                style={{
                    marginTop: 16,
                    display: "grid",
                    gap: 12,
                }}
            >
                <Info label="Proyecto" value={`${evidence.project_code || "PRJ"} · ${evidence.project_name || "Sin proyecto"}`} />
                <Info label="Tarea" value={`${evidence.wbs_code ? `${evidence.wbs_code} · ` : ""}${evidence.task_name || "Sin tarea"}`} />
                <Info label="Responsable" value={evidence.owner_name || "Sin responsable"} />
                <Info label="Revisor" value={evidence.reviewer_name || "Sin revisor"} />
                <Info label="Subido por" value={evidence.uploaded_by_name || evidence.uploaded_by_email || "Sin usuario"} />
                <Info label="Fecha" value={formatDate(evidence.created_at)} />
                <Info label="Estado de evidencia" value={getEvidenceStatusLabel(evidence)} />
                <Info label="Coordenadas" value={`${evidence.latitude}, ${evidence.longitude}`} />
            </div>

            {evidence.description || evidence.comment ? (
                <div
                    style={{
                        marginTop: 16,
                        padding: 14,
                        borderRadius: 18,
                        background: "#F8FAFC",
                        border: "1px solid #E2E8F0",
                        color: "#334155",
                        fontSize: 13,
                        fontWeight: 750,
                        lineHeight: 1.45,
                    }}
                >
                    {evidence.description || evidence.comment}
                </div>
            ) : null}

            <div
                style={{
                    marginTop: 18,
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                }}
            >
                <button type="button" onClick={() => onOpenTask?.(evidence)} style={primaryButton}>
                    Abrir tarea
                </button>

                <button type="button" onClick={() => onOpenMaps?.(evidence)} style={secondaryButton}>
                    Ver en Google Maps
                </button>
            </div>
        </div>
    );
}

function Info({ label, value }) {
    return (
        <div
            style={{
                padding: 12,
                borderRadius: 16,
                background: "#F8FAFC",
                border: "1px solid #E2E8F0",
            }}
        >
            <div
                style={{
                    color: "#64748B",
                    fontSize: 10,
                    fontWeight: 950,
                    textTransform: "uppercase",
                    letterSpacing: ".04em",
                }}
            >
                {label}
            </div>
            <div
                style={{
                    marginTop: 4,
                    color: "#0F172A",
                    fontSize: 13,
                    fontWeight: 900,
                    lineHeight: 1.35,
                }}
            >
                {value || "—"}
            </div>
        </div>
    );
}

const primaryButton = {
    height: 40,
    padding: "0 16px",
    borderRadius: 999,
    border: "1px solid #0F172A",
    background: "#0F172A",
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 14px 28px rgba(15,23,42,.18)",
};

const secondaryButton = {
    height: 40,
    padding: "0 16px",
    borderRadius: 999,
    border: "1px solid #CBD5E1",
    background: "#FFFFFF",
    color: "#0F172A",
    fontSize: 12,
    fontWeight: 950,
    cursor: "pointer",
};

export default function EvidenceMapPage() {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);
    const [selectedProjectId, setSelectedProjectId] = useState("all");
    const [selectedEvidenceId, setSelectedEvidenceId] = useState(null);

    const loadMap = async () => {
        try {
            setLoading(true);
            setError("");

            const token = localStorage.getItem("cani_auth") || "";
            const qp = new URLSearchParams();

            if (selectedProjectId && selectedProjectId !== "all") {
                qp.set("project_id", selectedProjectId);
            }

            const response = await fetch(`${API_BASE}/pmo_evidence_map.php${qp.toString() ? `?${qp.toString()}` : ""}`, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : "",
                    "X-CANI-AUTH": token || "",
                },
            });

            const text = await response.text();

            if (!text || !text.trim()) {
                throw new Error("Respuesta vacía del mapa de evidencias");
            }

            const json = JSON.parse(text);

            if (!response.ok || !json.success) {
                throw new Error(json.message || json.detail || "No se pudo cargar mapa de evidencias");
            }

            setData(json.data || null);

            const first = json.data?.evidences?.[0];
            setSelectedEvidenceId(first?.id || null);
        } catch (e) {
            setError(e.message || "Error al cargar mapa de evidencias");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadMap();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProjectId]);

    const evidences = data?.evidences || [];
    const projects = data?.projects || [];
    const projectFilterOptions = data?.project_options || projects;
    const summary = data?.summary || {};

    const selectedEvidence = useMemo(() => {
        return evidences.find((ev) => String(ev.id) === String(selectedEvidenceId)) || evidences[0] || null;
    }, [evidences, selectedEvidenceId]);

    const openTask = (evidence) => {
        if (!evidence?.task_id) return;

        const qp = new URLSearchParams();
        qp.set("mode", "progress");
        qp.set("return_to", "/evidence-map");
        qp.set("return_label", "Mapa de Evidencias");

        navigate(`/my-work/task/${evidence.task_id}?${qp.toString()}`);
    };

    const openMaps = (evidence) => {
        if (!evidence?.latitude || !evidence?.longitude) return;

        const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            `${evidence.latitude},${evidence.longitude}`
        )}`;

        window.open(url, "_blank", "noopener,noreferrer");
    };

    if (loading) {
        return (
            <div
                style={{
                    minHeight: "60vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#64748B",
                    fontSize: 14,
                    fontWeight: 850,
                }}
            >
                Cargando mapa de evidencias...
            </div>
        );
    }

    if (error) {
        return (
            <div
                style={{
                    borderRadius: 20,
                    background: "#FFF1F2",
                    border: "1px solid #FECDD3",
                    color: "#BE123C",
                    padding: 20,
                    fontSize: 14,
                    fontWeight: 850,
                }}
            >
                {error}
            </div>
        );
    }

    return (
        <div
            style={{
                minHeight: "100vh",
                background: "radial-gradient(circle at top left, rgba(37,99,235,.10), transparent 28%), #F8FAFC",
            }}
        >
            <section
                style={{
                    marginBottom: 18,
                    borderRadius: 28,
                    padding: 26,
                    background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 58%, #EEF2FF 100%)",
                    border: "1px solid #E2E8F0",
                    boxShadow: "0 20px 50px rgba(15,23,42,.07)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 20,
                    flexWrap: "wrap",
                }}
            >
                <div>
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "7px 11px",
                            borderRadius: 999,
                            background: "#ECFDF5",
                            border: "1px solid #A7F3D0",
                            color: "#047857",
                            fontSize: 11,
                            fontWeight: 950,
                            letterSpacing: ".06em",
                            textTransform: "uppercase",
                            marginBottom: 14,
                        }}
                    >
                        <span
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: 999,
                                background: "#16A34A",
                            }}
                        />
                        Evidencia / Campo / Trazabilidad
                    </div>

                    <h1
                        style={{
                            margin: 0,
                            fontSize: 34,
                            lineHeight: 1.05,
                            color: "#0F172A",
                            fontWeight: 950,
                            letterSpacing: "-.05em",
                        }}
                    >
                        Mapa de Evidencias PMO
                    </h1>

                    <p
                        style={{
                            margin: "10px 0 0",
                            color: "#64748B",
                            fontSize: 14,
                            lineHeight: 1.5,
                            maxWidth: 840,
                            fontWeight: 700,
                        }}
                    >
                        Visualiza dónde se registraron evidencias con coordenadas, quién las cargó, a qué tarea pertenecen
                        y cómo respaldan el avance operativo del proyecto. Vista actual: {getScopeLabel(selectedProjectId)}.
                    </p>
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(150px, 1fr))",
                        gap: 12,
                        minWidth: 360,
                    }}
                >
                    <KpiCard
                        title="Geoevidencias"
                        value={summary.total_geo_evidences || 0}
                        subtitle="Evidencias con coordenadas"
                        color="#16A34A"
                        icon="⌖"
                    />

                    <KpiCard
                        title="Proyectos"
                        value={summary.total_projects_with_geo || 0}
                        subtitle="Con evidencia en campo"
                        color="#2563EB"
                        icon="▦"
                    />

                    <KpiCard
                        title="Alcance PMO"
                        value={getScopeLabel(selectedProjectId)}
                        subtitle={selectedProjectId === "all" ? "Vista global de proyectos" : "Vista filtrada del proyecto"}
                        color="#7C3AED"
                        icon="◎"
                    />
                </div>
            </section>

            <section
                style={{
                    marginBottom: 18,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                    flexWrap: "wrap",
                }}
            >
                <select
                    value={selectedProjectId}
                    onChange={(e) => setSelectedProjectId(e.target.value)}
                    style={{
                        height: 42,
                        minWidth: 320,
                        borderRadius: 999,
                        border: "1px solid #E2E8F0",
                        background: "#FFFFFF",
                        padding: "0 16px",
                        color: "#0F172A",
                        fontSize: 13,
                        fontWeight: 800,
                        outline: "none",
                        boxShadow: "0 12px 25px rgba(15,23,42,.04)",
                    }}
                >
                    <option value="all">{getScopeLabel("all")}</option>
                    {projectFilterOptions.map((p) => (
                        <option key={p.id || p.project_id} value={p.id || p.project_id}>
                            {(p.name || p.project_name)} — {p.geo_evidences || 0} punto(s) registrados
                        </option>
                    ))}
                </select>

                <button type="button" onClick={loadMap} style={primaryButton}>
                    Actualizar mapa
                </button>
            </section>

            <section
                style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1.6fr) minmax(360px, .8fr)",
                    gap: 18,
                    alignItems: "start",
                }}
            >
                <EvidenceMapCanvas
                    evidences={evidences}
                    selectedEvidenceId={selectedEvidence?.id}
                    onSelect={(ev) => setSelectedEvidenceId(ev.id)}
                />

                <EvidenceCard evidence={selectedEvidence} onOpenTask={openTask} onOpenMaps={openMaps} />
            </section>
        </div>
    );
}
